/**
 * Título de página estándar: icono (opcionalmente clicable) + flecha › + nombre.
 * Mismo patrón que la página de inventario/existencias en todas las páginas.
 */

import { InlineStack, Text, Button, Icon } from '@/lib/polaris';
import type { IconSource } from '@/lib/polaris';

// IconSource (el tipo de Polaris), no un FunctionComponent angosto: este componente solo
// reenvia el icono a <Button icon> y <Icon source>, y AMBOS aceptan IconSource. Ademas los
// consumidores (listView.types.titleIcon, SettingsPage.icon) ya estan tipados asi — dos
// definiciones distintas del mismo concepto era exactamente la clase de bug de esta campana.
type PolarisIconType = IconSource;

export interface PageBreadcrumbTitleProps {
  /** Icono de la sección (opcional). Si no se define, el título se muestra sin chevron. */
  icon?: PolarisIconType;
  /** Nombre de la página (o nodo para títulos complejos) */
  title: React.ReactNode;
  /** Si se define, el icono es un botón que ejecuta esta acción (volver atrás) */
  onBack?: () => void;
  /** Etiqueta de accesibilidad para el botón "volver" */
  backAccessibilityLabel?: string;
  /** Contenido opcional a la derecha del título (ej. view switcher, badges). */
  titleMetadata?: React.ReactNode;
}

export function PageBreadcrumbTitle({
  icon,
  title,
  onBack,
  backAccessibilityLabel = 'Volver',
  titleMetadata,
}: PageBreadcrumbTitleProps) {
  return (
    <InlineStack gap="200" blockAlign="center" wrap={false}>
      {icon ? (
        onBack ? (
          <Button
            variant="plain"
            icon={icon}
            onClick={onBack}
            accessibilityLabel={backAccessibilityLabel}
          />
        ) : (
          <Icon source={icon} />
        )
      ) : null}
      {icon && (
        <Text variant="bodyMd" tone="subdued" as="span">
          ›
        </Text>
      )}
      {typeof title === 'string' ? (
        <Text variant="headingLg" fontWeight="semibold" as="h1">
          {title}
        </Text>
      ) : (
        title
      )}
      {titleMetadata}
    </InlineStack>
  );
}
